import React from "react";
import { useLiveData } from "../../common/utils";
import { useTranslation } from "react-i18next";
import {
  AppFlowContinueButton,
  AppFlowStepContainer,
  AppFlowStepContentContainer,
  AppFlowTitle,
} from "../../common/CommonStyles";
import {
  isInTrialMode,
  ReconcileStripeCheckoutSessionResult,
  StripeSubscriptionProductId,
} from "../../common/api";
import { useLoadingIndicator } from "../../common/context/LoadingIndicatorContext";
import { ApiContext } from "../../common/context/ApiContext";

import { loadStripe, Stripe } from "@stripe/stripe-js";
import {
  EmbeddedCheckoutProvider,
  EmbeddedCheckout,
} from "@stripe/react-stripe-js";
import { Account, AccountContext } from "../../common/context/AccountContext";
import ReactGA from "react-ga4";
import {
  AnalyticsContext,
  PurchaseFailedEvent,
  PurchasePendingEvent,
  PurchaseSuccessfulEvent,
  StripeCheckoutStartedEvent,
} from "../../common/context/AnalyticsContext";

const enum State {
  INITIAL,
  CREATING_CHECKOUT_SESSION,
  CHECKOUT_IN_PROGRESS,
  NEED_TO_RECONCILE,
  RECONCILING,
  SUCCESS,
  SESSION_EXPIRED,
  PENDING_PAYMENT,
  ERROR_RETRY_RECONCILIATION,
  ERROR_CANNOT_RETRY,
}

export const KEY_CHECKOUT_SESSION_ID = "csid";

export const StripeCheckout: React.FC<{
  selectedProductId?: StripeSubscriptionProductId;
  existingCheckoutSessionId?: string;
  next: () => void;
  restartUpgrade: () => void;
  exitUpgrade: () => void;
}> = ({
  selectedProductId,
  existingCheckoutSessionId,
  next,
  restartUpgrade,
  exitUpgrade,
}) => {
  const { t } = useTranslation();
  const { updateUser, accountLiveData } = React.useContext(AccountContext);
  const { logEvent } = React.useContext(AnalyticsContext);

  const [account, setAccount] = React.useState<Account>();

  // If have existing checkoutSessionId, then call reconcile API and do what it says.
  // Otherwise, call create session API and render Stripe UI
  const [state, setState] = React.useState<State>(State.INITIAL);
  const { startLoading, stopLoading } = useLoadingIndicator(false);
  const {
    createStripeCheckoutSession,
    getBakery,
    reconcileStripeCheckoutSession,
  } = React.useContext(ApiContext);
  const [stripePromise, setStripePromise] =
    React.useState<Promise<Stripe | null>>();
  const [checkoutSessionId, setCheckoutSessionId] = React.useState<string>();
  const [clientSecret, setClientSecret] = React.useState<string>();
  const [errorMessage, setErrorMessage] = React.useState<string>();

  useLiveData(
    React.useCallback(async (account: Account) => {
      if (account.hasUserAndMaybeProfile()) {
        setAccount(account);
      }
    }, []),
    accountLiveData
  );

  React.useEffect(() => {
    if (account?.user === undefined) {
      return;
    }
    if (!isInTrialMode(account.user) && state === State.INITIAL) {
      // This, and the previous account?.user === undefined accounts for the perceptron
      // code sign up flow. Basically, as a result of the flow, the user is expected to
      // already be al full user at this point, so we immediately show the success screen
      //
      // We cannot skip this stage in AppFlow based on !isInTrialMode because of how
      // we need to re-enter the flow after a successful stripe purchase. We also
      // don't just exit the flow because there may be stages after the checkout that
      // we wan't signed up users to see
      setState(State.SUCCESS);
    } else if (state === State.INITIAL && selectedProductId) {
      // We came here for a brand new checkout. Let's create a new session and render some Stripe.
      startLoading();
      setState(State.CREATING_CHECKOUT_SESSION);
      createStripeCheckoutSession(selectedProductId).then((session) => {
        setClientSecret(session.client_secret);
        setCheckoutSessionId(session.checkout_session_id);
        setStripePromise(loadStripe(session.publishable_api_key));
        setState(State.CHECKOUT_IN_PROGRESS);
        logEvent(new StripeCheckoutStartedEvent());
        stopLoading();
      });
    } else if (state === State.INITIAL && existingCheckoutSessionId) {
      // We came here with an existing checkout session. Let's reconcile it and figure out what to do next.
      setCheckoutSessionId(existingCheckoutSessionId);
      setState(State.NEED_TO_RECONCILE);
    } else if (
      state === State.INITIAL &&
      !selectedProductId &&
      !existingCheckoutSessionId
    ) {
      setState(State.ERROR_CANNOT_RETRY);
      setErrorMessage(t("checkout_unknown_error"));
    } else if (state === State.NEED_TO_RECONCILE && checkoutSessionId) {
      // Change state to avoid re-entering this block due to async calls triggering rerenders
      setState(State.RECONCILING);

      // We need to start reconciling the session. Call our backend and do what it tells us.
      startLoading();
      getBakery().then((bakery: Record<string, string>) =>
        reconcileStripeCheckoutSession(
          checkoutSessionId,
          bakery,
          window.location.href
        ).then((result: ReconcileStripeCheckoutSessionResult) => {
          if (result.result === "pending_checkout") {
            setClientSecret(result.client_secret);
            setStripePromise(loadStripe(result.publishable_api_key));
            setState(State.CHECKOUT_IN_PROGRESS);
            logEvent(new StripeCheckoutStartedEvent());
          } else if (result.result === "upgraded") {
            ReactGA.event("start_trial");
            updateUser(result.user, false, null, false);
            setState(State.SUCCESS);
            logEvent(new PurchaseSuccessfulEvent(result.product_id));
          } else if (result.result === "error_retry_reconciliation") {
            setState(State.ERROR_RETRY_RECONCILIATION);
            setErrorMessage(result.error_message);
            logEvent(new PurchaseFailedEvent(result.error_message));
          } else if (result.result === "error_cannot_retry") {
            setState(State.ERROR_CANNOT_RETRY);
            setErrorMessage(result.error_message);
            logEvent(new PurchaseFailedEvent(result.error_message));
          } else if (result.result === "pending_payment") {
            setState(State.PENDING_PAYMENT);
            logEvent(new PurchasePendingEvent());
          } else if (result.result === "expired") {
            setState(State.SESSION_EXPIRED);
            logEvent(new PurchaseFailedEvent("Checkout session expired"));
          }
          stopLoading();
        })
      );
    }
  }, [
    t,
    existingCheckoutSessionId,
    state,
    selectedProductId,
    checkoutSessionId,
    setState,
    setCheckoutSessionId,
    createStripeCheckoutSession,
    getBakery,
    reconcileStripeCheckoutSession,
    startLoading,
    stopLoading,
    updateUser,
    logEvent,
    account,
  ]);

  return state === State.CHECKOUT_IN_PROGRESS ? (
    <EmbeddedCheckoutProvider
      stripe={stripePromise!!}
      options={{
        clientSecret,
        onComplete: () => setState(State.NEED_TO_RECONCILE),
      }}
    >
      <EmbeddedCheckout className="h-100 mt-5" />
    </EmbeddedCheckoutProvider>
  ) : (
    <AppFlowStepContainer>
      <AppFlowTitle title={t("checkout_title")} />
      <AppFlowStepContentContainer
        style={{ paddingRight: "30px", paddingLeft: "30px" }}
      >
        {state === State.SUCCESS && (
          <>
            <div
              className="d-flex justify-content-center mt-4"
              style={{ maxHeight: "300px" }}
            >
              <img alt="" className="img-fluid" src={`${process.env.PUBLIC_URL}/teacher_arms_up.png`} />
            </div>
            <div className="d-flex justify-content-center mt-4">
              <h6>{t("checkout_all_set")}</h6>
            </div>
            <div className="d-flex justify-content-center mt-5">
              <AppFlowContinueButton
                text={t("app_flow_continue_button")}
                enabled={true}
                onClick={() => {
                  next();
                }}
              />
            </div>
          </>
        )}
        {state === State.ERROR_CANNOT_RETRY && (
          <>
            <div className="alert alert-danger" role="alert">
              <h6 className="alert-heading">{t("checkout_error")}</h6>
              <hr />
              <p>{errorMessage}</p>
            </div>
            <div className="d-flex justify-content-center mt-4">
              <AppFlowContinueButton
                text={t("generic_done")}
                enabled={true}
                onClick={() => {
                  exitUpgrade();
                }}
              />
            </div>
          </>
        )}
        {state === State.ERROR_RETRY_RECONCILIATION && (
          <>
            <div className="alert alert-warning" role="alert">
              <h6 className="alert-heading">{t("checkout_error")}</h6>
              <hr />
              <p>{errorMessage}</p>
            </div>
            <div className="d-flex justify-content-center mt-4">
              <button
                type="button"
                className="btn btn-outline-primary rounded-pill btn-lg px-5 mx-auto"
                onClick={() => setState(State.NEED_TO_RECONCILE)}
              >
                {t("checkout_check_again")}
              </button>
            </div>
          </>
        )}
        {state === State.PENDING_PAYMENT && (
          <>
            <div className="alert alert-light" role="alert">
              <h6 className="alert-heading">{t("checkout_pending_payment")}</h6>
              <hr />
              <p>{t("checkout_pending_payment_message")}</p>
            </div>
            <div className="d-flex justify-content-center mt-4 mb-3">
              <button
                type="button"
                className="btn btn-outline-primary rounded-pill btn-lg px-5 mx-auto"
                onClick={() => setState(State.NEED_TO_RECONCILE)}
              >
                {t("checkout_check_payment_status")}
              </button>
            </div>
          </>
        )}
        {state === State.SESSION_EXPIRED && (
          <>
            <div className="alert alert-warning" role="alert">
              <h6 className="alert-heading">{t("checkout_session_expired")}</h6>
              <hr />
              <p>{t("checkout_payment_incomplete")}</p>
            </div>
            <div className="d-flex justify-content-center mt-4">
              <AppFlowContinueButton
                text={t("checkout_restart")}
                enabled={true}
                onClick={() => restartUpgrade()}
              />
            </div>
          </>
        )}
      </AppFlowStepContentContainer>
    </AppFlowStepContainer>
  );
};
